import { BellRing, Film, ImageOff, Pause, Play, X } from "lucide-react";
import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import type {
  EventStats,
  Locale,
  PublicPost,
  TimelinePayload,
} from "@vzlive/shared";
import { t } from "@vzlive/shared/i18n";
import { ConsentEmbed } from "./ConsentEmbed";
import { ChatDock } from "./ChatDock";
import { Countdown } from "./Countdown";
import { SetlistStrip } from "./Setlist";
import { StickyAudio } from "./StickyAudio";
import { StatusBadge } from "./StatusBadge";
import { SubmissionForm } from "./SubmissionForm";
import { tenantTheme } from "../lib/branding";
import { partitionTimeline } from "../lib/timeline";
import { linkify } from "../lib/linkify";
import type { ChatStreamEvent } from "../hooks/useTimeline";

/** Beschreibung als Teaser: max. 3 Zeilen, nur wenn der Text wirklich
 *  umbrechen wird, erscheint „Mehr anzeigen" zum Ausklappen. */
function PulseDescription({ text, en }: { text: string; en: boolean }) {
  const textRef = useRef<HTMLParagraphElement>(null);
  const [clamped, setClamped] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (expanded) return;
    let cancelled = false;
    const measure = () => {
      if (cancelled || !textRef.current) return;
      const node = textRef.current;
      setClamped(node.scrollHeight > node.clientHeight + 1);
    };
    const raf = requestAnimationFrame(measure);
    if (document.fonts?.ready) void document.fonts.ready.then(measure);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
  }, [text, expanded]);

  if (!text.trim()) return null;

  return (
    <div className="pulse-top__desc">
      <p
        ref={textRef}
        className={clamped && !expanded ? "is-clamped" : undefined}
      >
        {text}
      </p>
      {clamped && (
        <button
          type="button"
          className="pulse-top__desc-toggle"
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded
            ? en
              ? "Show less"
              : "Weniger anzeigen"
            : en
              ? "Show more"
              : "Mehr anzeigen"}
        </button>
      )}
    </div>
  );
}

function timeLabel(date: string, locale: Locale, timezone?: string) {
  return new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: timezone,
  }).format(new Date(date));
}

function PostMedia({
  post,
  locale,
  onOpen,
  wide = false,
}: {
  post: PublicPost;
  locale: Locale;
  onOpen: (post: PublicPost, trigger: HTMLButtonElement) => void;
  wide?: boolean;
}) {
  const derivatives = post.media?.derivatives
    .filter((item) => item.format !== "original")
    .sort((first, second) => first.width - second.width);
  const thumb =
    derivatives?.find((item) => item.width >= 320) ?? derivatives?.at(-1);
  const portrait =
    thumb && thumb.height && thumb.width > 0
      ? thumb.height / thumb.width > 1.05
      : false;

  if (!derivatives?.length || !thumb) {
    return (
      <div className="pulse-post__media pulse-post__media--processing">
        <ImageOff size={20} aria-hidden="true" />
        <span>
          {locale === "en"
            ? "Image is being processed"
            : "Bild wird verarbeitet"}
        </span>
      </div>
    );
  }
  return (
    <button
      type="button"
      className={`pulse-post__media${portrait ? " pulse-post__media--portrait" : ""}${
        wide ? " pulse-post__media--wide" : ""
      }`}
      onClick={(e) => onOpen(post, e.currentTarget)}
      aria-label={locale === "en" ? "Enlarge image" : "Bild vergrößern"}
    >
      <picture>
        <source
          type="image/avif"
          srcSet={derivatives
            .filter((item) => item.format === "avif")
            .map((item) => `${item.url} ${item.width}w`)
            .join(", ")}
          sizes={wide ? "(max-width: 740px) 100vw, 600px" : "136px"}
        />
        <source
          type="image/webp"
          srcSet={derivatives
            .filter((item) => item.format === "webp")
            .map((item) => `${item.url} ${item.width}w`)
            .join(", ")}
          sizes={wide ? "(max-width: 740px) 100vw, 600px" : "136px"}
        />
        <img
          src={thumb.url}
          alt={post.media?.alt ?? ""}
          width={thumb.width ?? undefined}
          height={thumb.height || undefined}
          loading="lazy"
        />
      </picture>
      <span className="pulse-media__zoom" aria-hidden="true">
        ↗
      </span>
    </button>
  );
}

function PulsePost({
  post,
  locale,
  timezone,
  onOpen,
}: {
  post: PublicPost;
  locale: Locale;
  timezone?: string;
  onOpen: (post: PublicPost, trigger: HTMLButtonElement) => void;
}) {
  const stamp = (
    <div className="pulse-post__stamp">
      <time dateTime={post.publishedAt}>
        {timeLabel(post.publishedAt, locale, timezone)}
      </time>
      {post.byline && <span className="pulse-post__byline">{post.byline}</span>}
    </div>
  );

  // Pure media update: no headline, no copy — just the photo preview.
  if (post.type === "image" && !post.content && !post.embed) {
    return (
      <article className="pulse-post pulse-post--photo-only">
        {stamp}
        <PostMedia post={post} locale={locale} onOpen={onOpen} wide />
      </article>
    );
  }
  return (
    <article className="pulse-post">
      {stamp}
      <div
        className={`pulse-post__body${
          post.type !== "image" ? " pulse-post__body--text" : ""
        }`}
      >
        <div className="pulse-post__main">
          {post.content && (
            <p className="pulse-post__copy">{linkify(post.content)}</p>
          )}
          {post.type === "video" ? (
            post.video?.state === "ready" && post.embed ? (
              <div className="pulse-post__embed">
                <ConsentEmbed embed={post.embed} locale={locale} />
              </div>
            ) : (
              <div className="pulse-post__embed">
                <div className="video-state video-state--pulse" role="status">
                  <Film size={20} aria-hidden="true" />
                  <span>
                    {t(
                      locale,
                      post.video &&
                        (post.video.state === "failed" ||
                          post.video.state === "removed")
                        ? "videoFailed"
                        : "videoProcessing",
                    )}
                  </span>
                </div>
              </div>
            )
          ) : post.embed ? (
            <div className="pulse-post__embed">
              <ConsentEmbed embed={post.embed} locale={locale} />
            </div>
          ) : null}
        </div>
        {post.type === "image" && (
          <PostMedia post={post} locale={locale} onOpen={onOpen} />
        )}
      </div>
    </article>
  );
}

export function EmbedReader({
  timeline,
  stats,
  connectionState,
  onChatEvent,
}: {
  timeline: TimelinePayload;
  stats: EventStats | null;
  connectionState: "loading" | "ready" | "offline" | "error";
  onChatEvent: (listener: (event: ChatStreamEvent) => void) => () => void;
}) {
  const { tenant, event } = timeline;
  const locale = tenant.locale;
  const en = locale === "en";
  const setlistPlugin = timeline.plugins.find(
    (item) => item.id === "setlist.u2tour",
  );
  const { pinned, regular } = partitionTimeline(timeline.posts);
  const [frozen, setFrozen] = useState<PublicPost[] | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const previousIds = useRef(new Set(timeline.posts.map((post) => post.id)));
  const composerRef = useRef<HTMLDialogElement>(null);
  const lightboxRef = useRef<HTMLDialogElement>(null);
  const lastTrigger = useRef<HTMLElement | null>(null);
  const [lightbox, setLightbox] = useState<PublicPost | null>(null);

  const sorted = [...regular].sort(
    (first, second) =>
      Date.parse(second.publishedAt) - Date.parse(first.publishedAt),
  );
  const pending = frozen
    ? sorted.filter((post) => !frozen.some((old) => old.id === post.id))
    : [];
  const feed = frozen
    ? [...frozen].sort(
        (first, second) =>
          Date.parse(second.publishedAt) - Date.parse(first.publishedAt),
      )
    : sorted;

  useEffect(() => {
    const added = timeline.posts.filter(
      (post) => !previousIds.current.has(post.id),
    ).length;
    previousIds.current = new Set(timeline.posts.map((post) => post.id));
    if (!frozen && added)
      setAnnouncement(
        en
          ? `${added} new ${added === 1 ? "update" : "updates"} in the liveblog.`
          : `${added} ${added === 1 ? "neue Meldung" : "neue Meldungen"} im Live-Ticker.`,
      );
  }, [timeline.posts, frozen, en]);

  useEffect(() => {
    const onClose = () => lastTrigger.current?.focus();
    const dialogs = [composerRef.current, lightboxRef.current];
    dialogs.forEach((dialog) => dialog?.addEventListener("close", onClose));
    return () =>
      dialogs.forEach((dialog) =>
        dialog?.removeEventListener("close", onClose),
      );
  }, []);

  const eventDate = new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "long",
    year: "numeric",
    timeZone: event.timezone,
  }).format(new Date(event.startsAt));

  const openDialog = (
    ref: RefObject<HTMLDialogElement | null>,
    trigger: HTMLElement,
  ) => {
    lastTrigger.current = trigger;
    ref.current?.showModal();
  };
  const openLightbox = (post: PublicPost, trigger: HTMLButtonElement) => {
    setLightbox(post);
    openDialog(lightboxRef, trigger);
  };

  const renderPost = (post: PublicPost): ReactNode => (
    <PulsePost
      key={post.id}
      post={post}
      locale={locale}
      timezone={event.timezone}
      onOpen={openLightbox}
    />
  );

  return (
    <div
      className="reader-page reader-page--embed pulse"
      style={tenantTheme(tenant.primaryColor, tenant.secondaryColor)}
      aria-label={`${event.title} Live-Ticker`}
    >
      {connectionState === "offline" && (
        <div className="pulse-connection" role="status">
          {en
            ? "Connection interrupted. Loaded updates remain visible."
            : "Verbindung unterbrochen. Bereits geladene Beiträge bleiben sichtbar."}
        </div>
      )}

      {timeline.alert && (
        <aside className={`pulse-alert pulse-alert--${timeline.alert.tone}`}>
          <BellRing size={18} aria-hidden="true" />
          <strong>{timeline.alert.text}</strong>
        </aside>
      )}

      <header className="pulse-top">
        <div className="pulse-top__brandline">
          <span className="pulse-top__brand">
            {tenant.logoUrl ? (
              <img src={tenant.logoUrl} alt={tenant.name} />
            ) : (
              <strong>{tenant.name}</strong>
            )}
          </span>
          <StatusBadge status={event.status} locale={locale} />
        </div>
        <div className="pulse-top__title-row">
          <h1 className="pulse-top__title">{event.title}</h1>
          {tenant.submissionsEnabled && event.status !== "archived" && (
            <button
              type="button"
              className="pulse-button pulse-button--primary"
              onClick={(e) => openDialog(composerRef, e.currentTarget)}
            >
              <span aria-hidden="true">＋</span>{" "}
              {en ? "Send message" : "Nachricht senden"}
            </button>
          )}
        </div>
        <p className="pulse-top__context">
          <time dateTime={event.startsAt}>{eventDate}</time>
          {event.venue && <span> · {event.venue}</span>}
          {event.status !== "archived" && stats && (
            <span className="pulse-top__audience">
              <strong>{stats.onlineNow}</strong> {t(locale, "online")}
            </span>
          )}
        </p>
        {event.description?.trim() && (
          <PulseDescription text={event.description} en={en} />
        )}
        {event.status === "scheduled" &&
          new Date(event.startsAt) > new Date() && (
            <Countdown target={event.startsAt} locale={locale} />
          )}
      </header>

      {timeline.stickyMedia && (
        <StickyAudio inline media={timeline.stickyMedia} locale={locale} />
      )}

      <div
        className="pulse-layout"
        aria-label={en ? "Feed and chat" : "Feed und Chat"}
      >
        {setlistPlugin && (
          <SetlistStrip payload={setlistPlugin.payload} locale={locale} />
        )}

        <main
          id="feed"
          className="pulse-layout__stream"
          aria-label={t(locale, "timeline")}
        >
          {pinned.length > 0 && (
            <div className="pulse-pinfeed">
              {pinned.map((post) => (
                <PulsePost
                  key={post.id}
                  post={post}
                  locale={locale}
                  timezone={event.timezone}
                  onOpen={openLightbox}
                />
              ))}
            </div>
          )}
          <div className="pulse-feed-controls">
            {event.status !== "archived" &&
              (frozen ? (
                <button
                  type="button"
                  className="pulse-feed-controls__button"
                  onClick={() => setFrozen(null)}
                >
                  <Play size={16} aria-hidden="true" />{" "}
                  {en ? "Resume updates" : "Aktualisierung fortsetzen"}
                </button>
              ) : (
                <button
                  type="button"
                  className="pulse-feed-controls__button"
                  onClick={() => setFrozen(sorted)}
                >
                  <Pause size={16} aria-hidden="true" />{" "}
                  {en ? "Pause updates" : "Aktualisierung pausieren"}
                </button>
              ))}
          </div>

          {frozen && (
            <p className="pulse-feed-note" role="status" aria-atomic="true">
              {en ? "Updates paused." : "Aktualisierung pausiert."}
              {pending.length > 0 && (
                <>
                  {" "}
                  <button
                    type="button"
                    className="pulse-new-items"
                    onClick={() => setFrozen(null)}
                  >
                    ↑{" "}
                    {en
                      ? `${pending.length} new ${pending.length === 1 ? "update" : "updates"}`
                      : `${pending.length} ${pending.length === 1 ? "neue Meldung anzeigen" : "neue Meldungen anzeigen"}`}
                  </button>
                </>
              )}
            </p>
          )}

          {sorted.length === 0 && pinned.length === 0 ? (
            <div className="timeline-empty">{t(locale, "noPosts")}</div>
          ) : (
            feed.map(renderPost)
          )}
        </main>

        {tenant.chatEnabled &&
          event.chatEnabled &&
          event.status !== "archived" && (
            <ChatDock
              tenant={tenant}
              event={event}
              locale={locale}
              onChatEvent={onChatEvent}
              onlineNow={stats?.onlineNow}
            />
          )}
      </div>

      <footer className="pulse-foot">
        <span className="pulse-foot__state" role="status">
          <i aria-hidden="true" />
          {connectionState === "ready"
            ? en
              ? "Updates automatically"
              : "Aktualisierung automatisch"
            : en
              ? "Connection interrupted"
              : "Verbindung unterbrochen"}
        </span>
        <a
          className="pulse-foot__brand pulse-foot__brand--product"
          href="https://live.von2.media/"
          target="_blank"
          rel="noopener noreferrer"
        >
          {tenant.name} × VZ.live
        </a>
      </footer>

      <dialog
        ref={composerRef}
        className="pulse-dialog"
        aria-labelledby="pulse-compose-title"
      >
        <div className="pulse-dialog__head">
          <h2 id="pulse-compose-title">
            {en
              ? "Send status update to the editors"
              : "Status Update an die Redaktion senden"}
          </h2>
          <button
            type="button"
            className="pulse-dialog__close"
            aria-label={en ? "Close form" : "Formular schließen"}
            onClick={(e) => {
              composerRef.current?.close();
              e.currentTarget.blur();
            }}
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>
        <div className="pulse-dialog__content">
          <SubmissionForm eventId={event.id} locale={locale} compact />
        </div>
      </dialog>

      <dialog
        ref={lightboxRef}
        className="pulse-dialog pulse-dialog--image"
        aria-label={en ? "Image view" : "Bildansicht"}
        onClick={(e) => {
          if (e.target === e.currentTarget) lightboxRef.current?.close();
        }}
      >
        <div className="pulse-dialog__head">
          <h2>{en ? "Image view" : "Bildansicht"}</h2>
          <button
            type="button"
            className="pulse-dialog__close"
            aria-label={en ? "Close image view" : "Bildansicht schließen"}
            onClick={(e) => {
              lightboxRef.current?.close();
              e.currentTarget.blur();
            }}
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>
        {lightbox?.media && (
          <div className="pulse-dialog__image">
            <img
              src={
                lightbox.media.derivatives.at(-1)?.url ??
                lightbox.media.derivatives[0]?.url
              }
              alt={lightbox.media.alt ?? ""}
            />
          </div>
        )}
        <p className="pulse-dialog__caption">
          {lightbox?.media?.credit
            ? `${en ? "Photo" : "Foto"}: ${lightbox.media.credit}`
            : (lightbox?.media?.alt ?? "")}
        </p>
      </dialog>

      <div className="sr-only" role="status" aria-live="polite">
        {announcement}
      </div>
    </div>
  );
}
